import { type UseQueryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import type { NewVehicleGroup, UUID, VehicleGroup, VehicleGroupWithPath } from '@cobliteam/common-types';

import { GraphQLVehicleGroupsRequester, VehicleGroupsRequester } from '@cobliteam/api-lib';

import { Environment } from '@cobliteam/react-environments';

import useUser from '../useUser';

export const QUERY_KEY = 'VEHICLE_GROUP';

const env = Environment.getInstance().getEnvironment();

const vehicleGroupRequester = new VehicleGroupsRequester({ host: env.apiHost });
const graphQLVehicleGroupsRequester = new GraphQLVehicleGroupsRequester();

const useVehicleGroup = () => {
  const queryClient = useQueryClient();
  const { getSession } = useUser();
  const session = getSession();

  const fleetId = session.fleet.id;

  const invalidateGetQuery = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY, fleetId] });

  const deleteVehicleGroup = useMutation<UUID | void, unknown, UUID>({
    mutationFn: (id) => vehicleGroupRequester.deleteVehicleGroupFusca(id),
    onSuccess: () => invalidateGetQuery(),
  });

  const createVehicleGroup = useMutation<void, unknown, NewVehicleGroup>({
    mutationFn: async (newVehicleGroup) => graphQLVehicleGroupsRequester.createVehicleGroup(newVehicleGroup),
    onSuccess: () => invalidateGetQuery(),
  });

  const updateVehicleGroup = useMutation<void, unknown, VehicleGroup>({
    mutationFn: async (vehicleGroup) => graphQLVehicleGroupsRequester.updateVehicleGroup(vehicleGroup),
    onSuccess: () => invalidateGetQuery(),
  });

  const getVehicleGroups = (options?: Partial<UseQueryOptions<VehicleGroup[]>>, vehicleGroupsIds?: string[]) =>
    useSuspenseQuery<VehicleGroup[]>({
      queryKey: [QUERY_KEY, fleetId, vehicleGroupsIds],
      ...options,
      queryFn: async () => await graphQLVehicleGroupsRequester.getVehicleGroups(vehicleGroupsIds),
      refetchOnMount: false,
    });

  const getVehicleGroupsWithPath = (options?: Partial<UseQueryOptions<VehicleGroupWithPath[]>>) =>
    useQuery<VehicleGroupWithPath[]>({
      queryKey: [QUERY_KEY, fleetId],
      ...options,
      queryFn: async () => await graphQLVehicleGroupsRequester.getVehicleGroupsWithPaths(),
      refetchOnMount: false,
    });

  return {
    createVehicleGroup,
    updateVehicleGroup,
    getVehicleGroups,
    getVehicleGroupsWithPath,
    deleteVehicleGroup,
  };
};

export default useVehicleGroup;
